// src/components/VFXCanvas.tsx
import React, { useRef, useEffect, useState } from 'react';
import { useElementStore, ElementDef } from '../store/elementStore';

interface VFXCanvasProps {
  className?: string;
  onEffectComplete?: () => void;
}

type VFXState = {
  isPlaying: boolean;
  effectType: 'combine' | 'discover' | 'none';
  elements: ElementDef[];
  result?: ElementDef;
};

const VFXCanvas = React.forwardRef<
  { playCombineEffect: (resultId: string) => void },
  VFXCanvasProps
>(({ className = '', onEffectComplete }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<number>(0);
  const [vfxState, setVfxState] = useState<VFXState>({
    isPlaying: false,
    effectType: 'none',
    elements: [],
  });
  
  // Get element store data
  const all = useElementStore(s => s.all);
  const selected = useElementStore(s => s.selected);
  
  // Method to trigger combination effect
  const playCombineEffect = (resultId: string) => {
    // Get the elements being combined
    const elements = selected
      .filter(id => !!id)
      .map(id => all[id])
      .filter(Boolean);
    
    // Get the result element
    const result = all[resultId];
    
    if (elements.length >= 2 && result) {
      setVfxState({
        isPlaying: true,
        effectType: 'combine',
        elements,
        result
      });
    }
  };
  
  // Reset effect when done
  const resetEffect = () => {
    setVfxState({
      isPlaying: false,
      effectType: 'none',
      elements: []
    });
    
    if (onEffectComplete) {
      onEffectComplete();
    }
  };
  
  // Set up the animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Make canvas fill container
    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;
      }
    };
    
    // Resize on mount and window changes
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Get context for drawing
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    let animationId: number;
    let startTime = Date.now();
    
    const draw = () => {
      const elapsed = Date.now() - startTime;
      frameRef.current++;
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      if (vfxState.isPlaying) {
        const { effectType, elements, result } = vfxState;
        
        if (effectType === 'combine') {
          // Draw combination effect
          drawCombineEffect(ctx, canvas.width, canvas.height, elements, result!, elapsed);
          
          // End effect after 2.5 seconds
          if (elapsed > 2500) {
            resetEffect();
          }
        }
      }
      
      animationId = requestAnimationFrame(draw);
    };
    
    animationId = requestAnimationFrame(draw);
    
    // Cleanup
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [vfxState, onEffectComplete]);
  
  // Expose the play method to parent components
  React.useImperativeHandle(
    ref,
    () => ({
      playCombineEffect
    }),
    [selected, all]
  );
  
  // Helper function to draw combination effect
  const drawCombineEffect = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    elements: ElementDef[],
    result: ElementDef,
    elapsed: number
  ) => {
    const phase = Math.min(1, elapsed / 2000); // 0 to 1 over 2 seconds
    
    // Draw element orbs moving toward center
    elements.forEach((element, index) => {
      const total = elements.length;
      const angle = (index / total) * Math.PI * 2;
      
      // Calculate starting positions in a circle
      const startRadius = Math.min(width, height) * 0.35;
      const startX = width / 2 + Math.cos(angle) * startRadius;
      const startY = height / 2 + Math.sin(angle) * startRadius;
      
      // Move toward center based on phase
      const x = startX + (width / 2 - startX) * phase;
      const y = startY + (height / 2 - startY) * phase;
      
      // Draw element
      const size = Math.max(5, 40 - phase * 20); // Ensure minimum size of 5
      drawElementOrb(ctx, x, y, size, element, frameRef.current);
    });
    
    // After halfway point, start showing result
    if (phase > 0.5) {
      const resultPhase = (phase - 0.5) * 2; // 0 to 1 in second half
      const size = Math.max(5, 60 * resultPhase); // Ensure minimum size of 5
      const alpha = resultPhase;
      
      // Draw with transparency
      ctx.globalAlpha = alpha;
      drawElementOrb(ctx, width / 2, height / 2, size, result, frameRef.current);
      ctx.globalAlpha = 1;
      
      // Add magical effect
      drawMagicParticles(ctx, width / 2, height / 2, size + 20, resultPhase, frameRef.current);
    }
  };
  
  // Draw a single element orb
  const drawElementOrb = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    element: ElementDef,
    frame: number
  ) => {
    // Ensure positive size
    const safeSize = Math.max(1, size);
    
    // Draw basic circle
    ctx.beginPath();
    ctx.arc(x, y, safeSize, 0, Math.PI * 2);
    ctx.fillStyle = element.color || '#999';
    ctx.fill();
    
    // Add glow effect
    const glowSize = safeSize * 1.2;
    const gradient = ctx.createRadialGradient(x, y, safeSize * 0.8, x, y, glowSize);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 0.2)');
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
    
    ctx.beginPath();
    ctx.arc(x, y, glowSize, 0, Math.PI * 2);
    ctx.fillStyle = gradient;
    ctx.fill();
    
    // Add simple animation based on tier
    if (element.tier > 1) {
      const orbCount = Math.min(element.tier * 2, 8);
      const time = frame * 0.03;
      
      for (let i = 0; i < orbCount; i++) {
        const orbAngle = (i / orbCount) * Math.PI * 2 + time;
        const distance = safeSize * 0.8;
        const orbX = x + Math.cos(orbAngle) * distance;
        const orbY = y + Math.sin(orbAngle) * distance;
        const orbSize = Math.max(1, 2 + element.tier * 0.5);
        
        ctx.beginPath();
        ctx.arc(orbX, orbY, orbSize, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
        ctx.fill();
      }
    }
    
    // Draw border
    ctx.beginPath();
    ctx.arc(x, y, safeSize, 0, Math.PI * 2);
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 2;
    ctx.stroke();
  };
  
  // Draw magic particles effect
  const drawMagicParticles = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    radius: number,
    intensity: number,
    frame: number
  ) => {
    // Ensure positive radius
    const safeRadius = Math.max(1, radius);
    const particleCount = Math.floor(30 * intensity);
    const time = frame * 0.05;
    
    for (let i = 0; i < particleCount; i++) {
      const angle = (i / particleCount) * Math.PI * 2;
      const distance = safeRadius * (0.8 + Math.sin(time + i * 0.2) * 0.2);
      const particleX = x + Math.cos(angle + time * 0.5) * distance;
      const particleY = y + Math.sin(angle + time * 0.5) * distance;
      const size = Math.max(0.5, 1 + Math.sin(time + i) * 2);
      
      // Use a rainbow gradient for particles
      const hue = (i * 12 + time * 10) % 360;
      ctx.beginPath();
      ctx.arc(particleX, particleY, size, 0, Math.PI * 2);
      ctx.fillStyle = `hsla(${hue}, 100%, 70%, ${0.7 * intensity})`;
      ctx.fill();
    }
  };
  
  return <canvas ref={canvasRef} className={`absolute inset-0 ${className}`} />;
});

export default VFXCanvas;